// Surface itemization — one source shared by chairside + checkout so the
// per-surface breakdown of a filling always reads the same in both places.
//
// The clinic's Settings bundle % (procedures.additional_surface_pct) is the
// SSOT: the FIRST surface bills full catalog, each ADDITIONAL surface bills
// base × (% / 100). At 100% every surface is full price; at 60% ("40% off the
// second surface") extras are cheaper. The tiers below sum to exactly the same
// total as dos_v2.bundled_fee_centavos, so the money never changes — this only
// makes the derivation visible and auditable.

const SURFACE_ORDER = ["M", "O", "D", "B", "L", "I"];

export function splitSurfaces(s) {
  if (!s) return [];
  const set = new Set(String(s).toUpperCase().split("").filter((c) => SURFACE_ORDER.includes(c)));
  return SURFACE_ORDER.filter((c) => set.has(c));
}

export function surfaceCount(s) {
  return splitSurfaces(s).length;
}

// Returns [{ surface, priceCentavos, isFirst }] — first at full base, each
// additional at base × pct/100 (rounded per surface). Empty for < 1 surface.
export function surfaceBreakdown(surface, baseCentavos, additionalPct) {
  const surfaces = splitSurfaces(surface);
  const base = Math.max(0, Math.round(Number(baseCentavos) || 0));
  const pct = Number(additionalPct ?? 100);
  const addl = Math.round((base * pct) / 100);
  return surfaces.map((sf, i) => ({
    surface: sf,
    priceCentavos: i === 0 ? base : addl,
    isFirst: i === 0,
  }));
}

// Sum of the tiered breakdown, for reconciling against the item's stored price.
export function surfaceBreakdownTotal(surface, baseCentavos, additionalPct) {
  return surfaceBreakdown(surface, baseCentavos, additionalPct)
    .reduce((s, r) => s + r.priceCentavos, 0);
}

// True when the itemized tiers reconcile to the line's actual price (i.e. the
// price wasn't manually overridden to something custom). We only SHOW the
// per-surface breakdown when it honestly adds up to what's being charged.
export function surfaceBreakdownReconciles(surface, baseCentavos, additionalPct, priceCentavos) {
  const surfaces = splitSurfaces(surface);
  if (surfaces.length < 2) return false;                 // single/none: nothing to itemize
  if (!(Number(baseCentavos) > 0)) return false;
  return surfaceBreakdownTotal(surface, baseCentavos, additionalPct) === Math.round(Number(priceCentavos) || 0);
}
